import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Star, Clock, Truck, ChevronDown, ChevronUp } from 'lucide-react';

export default function BirthdayFlowersSydney() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState(false);

  // Search URL for birthday flowers
  const birthdaySearchUrl = "/search?location=Sydney+NSW&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8688&lng=151.2093&occasions=Birthday&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other";

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      script.onerror = () => setMapError(true);
      document.head.appendChild(script);
    };

    const initMap = () => {
      if (!mapRef.current) return;

      try {
        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: -33.8688, lng: 151.2093 },
          zoom: 11,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // Add marker for Sydney CBD
        new window.google.maps.Marker({
          position: { lat: -33.8688, lng: 151.2093 },
          map: map,
          title: 'Sydney CBD - Birthday Flower Delivery',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="12" fill="brand-primary"/>
                <circle cx="16" cy="16" r="8" fill="white"/>
                <circle cx="16" cy="16" r="4" fill="brand-primary"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapError(true);
      }
    };

    loadGoogleMaps();
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What are the best birthday flowers for different ages?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Children love bright, colorful arrangements with gerberas and sunflowers. Teenagers prefer trendy succulents or modern arrangements. Adults appreciate classic roses, elegant lilies, or sophisticated mixed bouquets."
        }
      },
      {
        "@type": "Question",
        "name": "Do Sydney florists offer same-day birthday flower delivery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, most Sydney florists offer same-day delivery for birthday flowers when orders are placed before 2pm on weekdays."
        }
      },
      {
        "@type": "Question",
        "name": "How much do birthday flowers cost in Sydney?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Birthday flower arrangements in Sydney range from $45 for simple bouquets to $150+ for elaborate arrangements, with most popular options between $65-95."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Birthday Flowers Sydney | Same-Day Flower Delivery | MarketBloom</title>
        <meta name="description" content="Find the perfect birthday flowers in Sydney. From vibrant bouquets for kids to elegant arrangements for adults, discover local florists who create memorable birthday surprises." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="pt-20 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 text-sm text-black mb-4">
              <span className="bg-[#967F81] text-white px-3 py-1 rounded-full font-medium">Occasions</span>
              <span>•</span>
              <span>20 January 2024</span>
              <span>•</span>
              <span>6 min read</span>
            </div>
            <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight text-black lg:text-5xl">
              Best Birthday Flowers Sydney - Perfect for Every Age 2025
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          
          {/* Hero Image */}
          <div className="mb-12">
            <img 
              src="/images/blog/birthday-flowers-sydney-hero.jpg" 
              alt="Colorful birthday flower arrangements at a Sydney florist with balloons and celebration decorations"
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <p className="text-xl leading-7 text-black mb-6">
              I think you'll agree with me when I say:
            </p>
            <p className="leading-7 text-black mb-6">
              Finding the perfect birthday flowers in Sydney shouldn't mean settling for generic supermarket bouquets or overpriced corporate chains that don't understand the personal touch birthdays deserve.
            </p>
            <p className="leading-7 text-black mb-6">
              That's why MarketBloom exclusively partners with independent Sydney florists who specialize in birthday celebrations - from the creative studios in Newtown crafting whimsical arrangements for children to the elegant boutiques in Double Bay creating sophisticated designs for milestone birthdays.
            </p>
            <p className="leading-7 text-black">
              Whether you need bright, cheerful flowers for a child's party, trendy arrangements for a teenager, romantic roses for a partner, or elegant bouquets for milestone celebrations, our network of local florists ensures every birthday becomes a memorable moment with fresh, beautiful blooms.
            </p>
          </div>

          {/* Quick Action CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 mb-12 text-center">
            <h2 className="text-2xl font-bold mb-3">Need Birthday Flowers Today?</h2>
            <p className="text-lg mb-4 opacity-90">Browse Sydney's finest florists offering same-day birthday flower delivery</p>
            <Link 
              to={`${birthdaySearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=birthday-flowers`}
              className="bg-white text-brand-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Birthday Flowers
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* What Makes Birthday Flowers Special */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Choosing Birthday Flowers by Age Group</h2>
            
            <p className="leading-7 text-black mb-6">
              Sydney's best florists understand that different age groups appreciate different styles of birthday flowers. Children typically love bright, colorful arrangements with flowers like gerberas, sunflowers, and rainbow roses that match their joyful energy. Teenagers might prefer trendy succulents, modern arrangements in their favorite colors, or Instagram-worthy designs they can share with friends.
            </p>
            <p className="leading-7 text-black mb-6">
              Adults often appreciate classic roses, elegant lilies, or sophisticated mixed bouquets that reflect their personal style. For milestone birthdays like 30th, 40th, or 50th celebrations, premium arrangements with luxury flowers like orchids, peonies, or imported blooms make the occasion extra special. Our Sydney florists excel at creating age-appropriate designs that capture the personality and preferences of each birthday celebrant.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-brand-primary" aria-label="Icon for children's flowers" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Children's Birthdays</h3>
                <p className="text-black">Bright, colorful arrangements with fun flowers</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-brand-primary" aria-label="Icon for adult celebrations" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Adult Celebrations</h3>
                <p className="text-black">Elegant roses, lilies, and sophisticated designs</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-brand-primary" aria-label="Icon for milestone birthdays" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Milestone Birthdays</h3>
                <p className="text-black">Premium arrangements with luxury flowers</p>
              </div>
            </div>
          </div>

          {/* Birthday Flower Delivery Coverage */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Same-Day Birthday Flower Delivery</h2>
            
            <p className="leading-7 text-black mb-6">
              Forgot someone's birthday? Sydney's local florists offer same-day delivery for those last-minute celebrations. Most florists accept same-day orders until 2 PM, creating fresh arrangements that arrive beautifully presented and ready to surprise the birthday celebrant.
            </p>

            <div className="mb-6">
              <div 
                ref={mapRef} 
                className="w-full h-80 rounded-lg shadow-md bg-gray-200"
              >
                {mapError && (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <MapPin className="w-8 h-8 mr-2" />
                    <span>Map unavailable - Sydney NSW</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">CBD & City</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Eastern Suburbs</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Inner West</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">North Shore</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Northern Beaches</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Western Sydney</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Southern Sydney</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Hills District</span>
              </div>
            </div>
          </div>

          {/* Popular Birthday Flower Styles */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Most Popular Birthday Flower Choices</h2>
            
            <p className="leading-7 text-black mb-6">
              Roses remain the most popular choice for birthday flowers, available in every color to match personal preferences and age groups. Gerberas bring joy with their bright, cheerful faces, perfect for children and young adults. Lilies add elegance and fragrance, ideal for sophisticated celebrations. Mixed bouquets allow florists to create unique combinations that reflect the recipient's personality and the celebratory nature of birthdays.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight text-black mb-4">Top Birthday Flower Choices:</h3>
              <ul className="my-6 ml-6 list-disc [&>li]:mt-2">
                <li className="text-black">Classic roses in favorite colors for romantic partners</li>
                <li className="text-black">Bright gerberas and sunflowers for children and cheerful celebrations</li>
                <li className="text-black">Elegant lilies and orchids for sophisticated milestone birthdays</li>
                <li className="text-black">Mixed seasonal bouquets for personalized arrangements</li>
              </ul>
            </div>
          </div>

          {/* Why Choose MarketBloom for Birthday Flowers */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Why MarketBloom Excels at Birthday Flower Delivery</h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Age-Appropriate Design Expertise</h3>
                  <p className="leading-7 text-black">Our Sydney florists understand how to create birthday arrangements that perfectly match the recipient's age and personality. From whimsical designs for children to sophisticated arrangements for adults, they know exactly what works for each celebration.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Personal Touch and Customization</h3>
                  <p className="leading-7 text-black">Local florists excel at personalizing birthday arrangements with favorite colors, special add-ons like balloons or chocolates, and custom designs that reflect hobbies or interests. Many also offer beautiful vases that become lasting keepsakes.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Same-Day Birthday Surprise Service</h3>
                  <p className="leading-7 text-black">Forgot someone's birthday? Our florists specialize in last-minute celebrations, creating fresh arrangements for same-day delivery to offices, homes, or even restaurants where birthday celebrations are taking place.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Budget-Friendly Options</h3>
                  <p className="leading-7 text-black">Beautiful birthday flowers don't have to break the bank. Our florists offer arrangements at various price points, from simple but elegant single-variety bouquets to elaborate mixed arrangements, with seasonal flowers providing the best value.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">5</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Celebration Expertise</h3>
                  <p className="leading-7 text-black">Our Sydney florists understand that birthdays are about joy and celebration. They create arrangements that capture the festive spirit, whether it's a child's first birthday or a grandparent's 80th milestone celebration.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Internal Linking Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Birthday Flower Delivery to Sydney Suburbs</h2>
            
            <p className="leading-7 text-black mb-6">
              Need birthday flowers delivered to specific Sydney areas? Our local florists provide celebration flower delivery across the city:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/search?location=Newtown+NSW+2042&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8978149&lng=151.1785003&occasions=Birthday&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Newtown birthday flowers - creative and whimsical arrangements
              </Link>
              <Link to="/search?location=Bondi+NSW+2026&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8915&lng=151.2767&occasions=Birthday&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Bondi birthday flowers - coastal-inspired celebration blooms
              </Link>
              <Link to="/search?location=Parramatta+NSW+2150&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8175438&lng=151.0000257&occasions=Birthday&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Parramatta birthday flowers - Western Sydney celebration service
              </Link>
              <Link to="/search?location=Surry+Hills+NSW+2010&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8886109&lng=151.2094393&occasions=Birthday&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Surry Hills birthday flowers - trendy inner-city celebrations
              </Link>
              <Link to="/search?location=Marrickville+NSW+2204&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.9113&lng=151.1553&occasions=Birthday&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Marrickville birthday flowers - multicultural celebration designs
              </Link>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-neutral-50 rounded-lg px-6 py-5 mb-8 shadow-sm">
            <blockquote className="italic text-black">
              <p className="leading-7 mb-2">
                "The birthday flowers were absolutely perfect for my daughter's 16th! The florist created exactly what a teenager would love - modern, Instagram-worthy, and delivered right on time."
              </p>
              <footer className="text-sm font-medium not-italic">
                – Sarah M., Sydney
              </footer>
            </blockquote>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 0}
                  aria-controls="faq-answer-0"
                >
                  <h3 className="text-xl font-semibold text-black">What are the best birthday flowers for different ages?</h3>
                  {openFaq === 0 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-0" className={openFaq === 0 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Children love bright, colorful arrangements with gerberas and sunflowers. Teenagers prefer trendy succulents or modern arrangements. Adults appreciate classic roses, elegant lilies, or sophisticated mixed bouquets.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 1}
                  aria-controls="faq-answer-1"
                >
                  <h3 className="text-xl font-semibold text-black">Do Sydney florists offer same-day birthday flower delivery?</h3>
                  {openFaq === 1 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-1" className={openFaq === 1 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Yes, most Sydney florists offer same-day delivery for birthday flowers when orders are placed before 2pm on weekdays.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 2}
                  aria-controls="faq-answer-2"
                >
                  <h3 className="text-xl font-semibold text-black">How much do birthday flowers cost in Sydney?</h3>
                  {openFaq === 2 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-2" className={openFaq === 2 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Birthday flower arrangements in Sydney range from $45 for simple bouquets to $150+ for elaborate arrangements, with most popular options between $65-95.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-[brand-primary] to-[#E63E1C] text-white rounded-lg p-8 text-center md:relative sticky bottom-0 z-50">
            <h2 className="scroll-m-20 text-3xl font-semibold tracking-tight text-white mb-4">Ready to Order Birthday Flowers?</h2>
            <p className="text-xl leading-7 text-white mb-6 opacity-90">
              Browse Sydney's finest florists offering same-day birthday flower delivery
            </p>
            <Link 
              to={`${birthdaySearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=birthday-flowers`}
              className="bg-white text-brand-primary px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-3 text-lg min-h-[48px]"
            >
              Shop Birthday Flowers Now
              <ArrowRight className="w-6 h-6" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}